import { Suspense } from 'react'
import { Navigate, BrowserRouter, Route, Routes } from 'react-router-dom'

import { lazyRoute } from '@/app/lazy-route'
import { AppLayout } from '@/app/layout/app-layout'
import { ProtectedRoute } from '@/features/auth/components/protected-route'
import { InviteAcceptPage } from '@/features/auth/pages/invite-accept-page'
import { TransferAcceptPage } from '@/features/auth/pages/transfer-accept-page'
import { LoginPage } from '@/features/auth/pages/login-page'
import { ForgotPasswordPage } from '@/features/auth/pages/forgot-password-page'
import { ImpersonatePage } from '@/features/auth/pages/impersonate-page'
import { ResetPasswordPage } from '@/features/auth/pages/reset-password-page'
import { RegisterPage } from '@/features/auth/pages/register-page'
import { ProjectLayout } from '@/features/projects/layout/project-layout'
import { ProjectsPage } from '@/features/projects/pages/projects-page'
import { LoadingBlock } from '@/shared/components/common/loading-block'
import { NotFoundPage } from '@/shared/components/common/not-found-page'

// Тяжёлые маршруты грузим лениво — они не нужны в стартовом бандле.
// Стартовый шелл (auth, layout, список проектов) остаётся в основном чанке.
// lazyRoute переживает пропавший после деплоя чанк: повтор + разовая перезагрузка.
const OperatorInboxPage = lazyRoute(
  () => import('@/features/projects/pages/operator-inbox-page'),
  'OperatorInboxPage',
)
const LeadsPage = lazyRoute(() => import('@/features/leads/pages/leads-page'), 'LeadsPage')
const BillingPage = lazyRoute(() => import('@/features/billing/pages/billing-page'), 'BillingPage')
const PartnerProgramRoute = lazyRoute(
  () => import('@/features/partners/partners-routes'),
  'PartnerProgramRoute',
)
const FakePayPage = lazyRoute(() => import('@/features/billing/pages/fake-pay-page'), 'FakePayPage')
const BillingReturnPage = lazyRoute(
  () => import('@/features/billing/pages/billing-return-page'),
  'BillingReturnPage',
)
const AccountPage = lazyRoute(() => import('@/features/settings/pages/account-page'), 'AccountPage')
const AvitoCallbackPage = lazyRoute(
  () => import('@/features/avito/pages/avito-callback-page'),
  'AvitoCallbackPage',
)
const VkCallbackPage = lazyRoute(
  () => import('@/features/vk/pages/vk-callback-page'),
  'VkCallbackPage',
)
const AvitoListingsPage = lazyRoute(
  () => import('@/features/avito/pages/avito-listings-page'),
  'AvitoListingsPage',
)

// Все разделы проекта живут в одном модуле — общий dynamic import даёт один
// чанк «sections», который подгружается при входе в проект.
const sections = () => import('@/features/projects/pages/sections')
const LegacyProjectRedirect = lazyRoute(sections, 'LegacyProjectRedirect')
const OverviewSection = lazyRoute(sections, 'OverviewSection')
const PromptsSection = lazyRoute(sections, 'PromptsSection')
const KnowledgeSection = lazyRoute(sections, 'KnowledgeSection')
const ChunkLinksSection = lazyRoute(sections, 'ChunkLinksSection')
const ProductsSection = lazyRoute(sections, 'ProductsSection')
const ConversationsSection = lazyRoute(sections, 'ConversationsSection')
const LeadsSection = lazyRoute(sections, 'LeadsSection')
const WidgetSection = lazyRoute(sections, 'WidgetSection')
const FineTuningSection = lazyRoute(sections, 'FineTuningSection')
const IntegrationsSection = lazyRoute(sections, 'IntegrationsSection')
const TrainingSection = lazyRoute(sections, 'TrainingSection')
const ProjectSettingsSection = lazyRoute(sections, 'ProjectSettingsSection')
const PolicySection = lazyRoute(sections, 'PolicySection')
const ProjectCheckSection = lazyRoute(sections, 'ProjectCheckSection')

export function AppRouter() {
  return (
    <BrowserRouter>
      <Suspense fallback={<LoadingBlock label="Загрузка..." />}>
        <Routes>
          <Route element={<LoginPage />} path="/login" />
          <Route element={<RegisterPage />} path="/register" />
          <Route element={<ForgotPasswordPage />} path="/forgot-password" />
          <Route element={<ResetPasswordPage />} path="/reset-password" />
          <Route element={<ImpersonatePage />} path="/impersonate" />
          <Route element={<InviteAcceptPage />} path="/invite/:token" />
          <Route element={<TransferAcceptPage />} path="/transfer/:token" />

          <Route element={<ProtectedRoute />}>
            <Route element={<AppLayout />}>
              <Route element={<Navigate replace to="/projects" />} index />
              <Route element={<OperatorInboxPage />} path="/operator" />
              <Route element={<LeadsPage />} path="/leads" />
              <Route element={<ProjectsPage />} path="/projects" />
              <Route element={<ProjectLayout />} path="/projects/:projectId">
                <Route element={<LegacyProjectRedirect />} index />
                <Route element={<OverviewSection />} path="overview" />
                <Route element={<PromptsSection />} path="prompts" />
                <Route element={<KnowledgeSection />} path="knowledge" />
                <Route element={<ChunkLinksSection />} path="chunk-links" />
                <Route element={<ProductsSection />} path="products" />
                <Route element={<ConversationsSection />} path="conversations" />
                <Route element={<LeadsSection />} path="leads" />
                <Route element={<WidgetSection />} path="widget" />
                <Route element={<FineTuningSection />} path="fine-tuning" />
                <Route element={<IntegrationsSection />} path="integrations" />
                <Route element={<TrainingSection />} path="training" />
                <Route element={<ProjectSettingsSection />} path="settings" />
                <Route element={<PolicySection />} path="policy" />
                <Route element={<ProjectCheckSection />} path="audit" />
                <Route element={<NotFoundPage />} path="*" />
              </Route>
              <Route element={<Navigate replace to="/account" />} path="/settings" />
              <Route element={<AccountPage />} path="/account" />
              <Route element={<BillingPage />} path="/billing" />
              <Route element={<BillingReturnPage />} path="/billing/return" />
              <Route element={<AvitoListingsPage />} path="/avito" />
              <Route element={<AvitoCallbackPage />} path="/avito/callback" />
              <Route element={<VkCallbackPage />} path="/vk/callback" />
              <Route element={<PartnerProgramRoute />} path="/partner-program" />
              <Route element={<FakePayPage />} path="/billing/fake-pay/:paymentId" />
            </Route>
          </Route>

          <Route element={<NotFoundPage />} path="*" />
        </Routes>
      </Suspense>
    </BrowserRouter>
  )
}
